// Check-in nachtragen (14.10.2026, backend/checkin_nachtrag.py): nur Leitung, mit Grund, höchstens 30 Tage zurück.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarPlus } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';

const berlinHeute = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const gestern = () => {
  const d = new Date(`${berlinHeute()}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};
const uhr = (iso) => new Date(iso).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });

export default function CheckinNachtragen({ memberId, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [datum, setDatum] = useState(gestern());
  const [lage, setLage] = useState(null);
  const [kurs, setKurs] = useState('');
  const [uhrzeit, setUhrzeit] = useState('18:00');
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!offen || !datum) return;
    setLage(null);
    setKurs('');
    api.get('/checkins/nachtragen/lage', { params: { member_id: memberId, datum } })
      .then((r) => setLage(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Tag konnte nicht geladen werden'));
  }, [offen, datum, memberId]);

  const speichern = async () => {
    setLaeuft(true);
    try {
      await api.post('/checkins/nachtragen', {
        member_id: memberId, datum, grund, ...(kurs ? { course_id: kurs } : { uhrzeit }),
      });
      toast.success('Check-in nachgetragen');
      setOffen(false);
      setGrund('');
      onFertig?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nachtragen fehlgeschlagen');
    } finally {
      setLaeuft(false);
    }
  };

  if (!offen) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOffen(true)} data-testid="checkin-nachtragen-oeffnen">
        <CalendarPlus className="w-4 h-4 mr-1.5" /> Check-in nachtragen
      </Button>
    );
  }

  const min = (() => { const d = new Date(`${berlinHeute()}T12:00:00`); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); })();
  const kurse = (lage?.kurse || []).filter((k) => !k.ausgefallen);
  return (
    <Card className="border-border/60" data-testid="checkin-nachtragen">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-sm">Check-in nachtragen</p>
          <Button variant="ghost" size="sm" onClick={() => setOffen(false)}>Schließen</Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Für einen vergessenen Scan. Zählt als Training wie ein normaler Check-in, öffnet keine Tür und wird mit deinem Namen und dem Grund festgehalten.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="nt-datum">Tag</Label>
            <Input id="nt-datum" type="date" value={datum} min={min} max={berlinHeute()} onChange={(e) => setDatum(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="nt-kurs">Kurs</Label>
            <select id="nt-kurs" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={kurs} onChange={(e) => setKurs(e.target.value)} disabled={!lage || !!lage.vorhanden}>
              <option value="">Freies Training (Uhrzeit angeben)</option>
              {kurse.map((k) => <option key={k.id} value={k.id}>{k.start} {k.name}</option>)}
            </select>
          </div>
          {!kurs && (
            <div className="space-y-1">
              <Label htmlFor="nt-uhr">Uhrzeit</Label>
              <Input id="nt-uhr" type="time" value={uhrzeit} onChange={(e) => setUhrzeit(e.target.value)} />
            </div>
          )}
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="nt-grund">Grund</Label>
            <Input id="nt-grund" value={grund} placeholder="z. B. Scan am Eingang vergessen, war im Training"
              onChange={(e) => setGrund(e.target.value)} />
          </div>
        </div>
        {lage?.vorhanden && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            An diesem Tag ist schon ein Check-in eingetragen ({uhr(lage.vorhanden.checked_in_at)} Uhr
            {lage.vorhanden.checked_in_by_name ? `, ${lage.vorhanden.checked_in_by_name}` : ''}). Nichts nachzutragen.
          </p>
        )}
        <Button size="sm" onClick={speichern} disabled={laeuft || !lage || !!lage.vorhanden || grund.trim().length < 5}
          data-testid="checkin-nachtragen-speichern">
          {laeuft ? 'Speichere …' : 'Nachtragen'}
        </Button>
      </CardContent>
    </Card>
  );
}
